import { useEffect, useState } from 'react'
import { enrichProCompanies, loadProCompanies, type ProCompany } from './repository/proCompaniesRepository'
import { companyState, type ProState } from './proCompanyState'

/**
 * The Skida PRO companies with their state, for screens outside the dashboard
 * — 22 Oct 2026.
 *
 * The Sales CRM shows each linked lead's state and a Skida PRO card, and both
 * read the same companies the dashboard does, through the same loader, so a
 * state can never differ between the two screens. Loaded once per page load
 * and shared: the leads table and the open lead ask for it together.
 *
 * Read-only against the product project. A failed read leaves `error` set and
 * every state unknown — the table then shows a dash rather than a guess.
 */
export interface ProCompanyView {
  company: ProCompany
  state: ProState | null
}

export interface ProCompaniesState {
  byId: Map<string, ProCompanyView>
  loading: boolean
  error: string | null
}

let shared: Promise<Map<string, ProCompanyView>> | null = null

function load(): Promise<Map<string, ProCompanyView>> {
  shared ??= loadProCompanies()
    .then((list) => enrichProCompanies(list))
    .then((list) => {
      const now = Date.now()
      return new Map(list.map((c) => [c.id, { company: c, state: companyState(c, now) }]))
    })
    .catch((e: unknown) => {
      // A failure is not kept: the next screen to ask tries again.
      shared = null
      throw e
    })
  return shared
}

export function useProCompanies(): ProCompaniesState {
  const [state, setState] = useState<ProCompaniesState>({ byId: new Map(), loading: true, error: null })
  useEffect(() => {
    let live = true
    load()
      .then((byId) => live && setState({ byId, loading: false, error: null }))
      .catch((e: unknown) =>
        live &&
        setState({
          byId: new Map(),
          loading: false,
          error: e instanceof Error ? e.message : 'Could not read Skida PRO.',
        }),
      )
    return () => {
      live = false
    }
  }, [])
  return state
}
